import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, Globe, Heart, Briefcase, Wallet, GraduationCap, Users, Plane, Sparkles, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// Domain icons mapping
const DOMAIN_ICONS = {
  health: Heart,
  career: Briefcase,
  wealth: Wallet,
  marriage: Heart,
  education: GraduationCap,
  family: Users,
  travel: Plane,
  spirituality: Sparkles
};

// Planet colors
const PLANET_COLORS = {
  Sun: '#FFD700', Moon: '#C8C8FF', Mars: '#DC143C', Mercury: '#32CD32',
  Jupiter: '#FFA500', Venus: '#FF69B4', Saturn: '#8B4513', Rahu: '#4B0082', Ketu: '#006400'
};

const PLANET_ICONS = {
  Sun: '☉', Moon: '☽', Mars: '♂', Mercury: '☿', Jupiter: '♃',
  Venus: '♀', Saturn: '♄', Rahu: '☊', Ketu: '☋'
};

const LANGUAGES = [
  { key: 'hindi', label: 'हिंदी' },
  { key: 'hinglish', label: 'Hinglish' },
  { key: 'english', label: 'English' }
];

const PastStoryAnimated = () => {
  const navigate = useNavigate();
  // Core state
  const [isLoading, setIsLoading] = useState(true);
  const [loadingPhase, setLoadingPhase] = useState('init'); // init, domains, mahadashas, complete
  const [currentLoadingDomain, setCurrentLoadingDomain] = useState(0);
  const [language, setLanguage] = useState('hinglish');
  
  // Data state
  const [userInfo, setUserInfo] = useState(null);
  const [domainOverviews, setDomainOverviews] = useState([]);
  const [mahadashaTimeline, setMahadashaTimeline] = useState([]);
  const [error, setError] = useState(null);
  
  // UI state
  const [expandedMahadasha, setExpandedMahadasha] = useState(null);
  const [showDomainDetails, setShowDomainDetails] = useState(null);

  // Load data on mount
  useEffect(() => {
    const loadStoryData = async () => {
      try {
        console.log('🔄 Loading story data...');
        
        // Get birth data from localStorage
        const birthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
        console.log('📋 Birth details found:', !!birthDetails);
        
        if (!birthDetails) {
          setError('No birth details found. Please enter your details first.');
          setIsLoading(false);
          return;
        }
        
        const birthData = JSON.parse(birthDetails);
        console.log('📋 Birth data:', birthData.name);
        setLoadingPhase('domains');
        
        // Fetch from API with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout
        
        console.log('🌐 Calling API...');
        const response = await fetch(`${API_URL}/api/hybrid-story/domain-overview-stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ birth_data: birthData }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        console.log('📡 Response status:', response.status);
        
        const result = await response.json();
        console.log('📦 Result success:', result.success);
        
        if (!result.success) {
          throw new Error(result.detail || 'Failed to load story');
        }
        
        setUserInfo(result.user_info);
        
        // Animate domain loading one by one
        const domains = result.domain_overviews || [];
        console.log('📊 Domains received:', domains.length);
        
        for (let i = 0; i < domains.length; i++) {
          setCurrentLoadingDomain(i);
          setDomainOverviews(prev => [...prev, domains[i]]);
          await new Promise(r => setTimeout(r, 300)); // 300ms delay between each
        }
        
        setLoadingPhase('mahadashas');
        await new Promise(r => setTimeout(r, 300));
        
        // Load mahadashas
        setMahadashaTimeline(result.mahadasha_timeline || []);
        console.log('🕉️ Mahadashas received:', result.mahadasha_timeline?.length);
        
        setLoadingPhase('complete');
        setIsLoading(false);
        
      } catch (err) {
        console.error('❌ Error loading story:', err);
        setError(err.message || 'Failed to load story. Please try again.');
        setIsLoading(false);
      }
    };
    
    loadStoryData();
  }, []);

  // Domain card animation variants
  const domainCardVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    })
  };

  // Mahadasha card variants
  const mahadashaVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.4
      }
    })
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/your-story')}
            className="px-6 py-2 bg-amber-500/20 border border-amber-500/50 rounded-full text-amber-300"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white overflow-x-hidden">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50 px-4 py-3">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div>
            <h1 className="text-lg font-semibold text-amber-300">
              {userInfo?.name || 'Your'}'s Past Story
            </h1>
            <p className="text-xs text-slate-400">
              8 Life Domains • {mahadashaTimeline.length} Mahadashas
            </p>
          </div>
          
          {/* Language Selector */}
          <div className="flex gap-1 bg-slate-800/50 rounded-full p-1">
            {LANGUAGES.map(lang => (
              <button
                key={lang.key}
                onClick={() => setLanguage(lang.key)}
                className={`px-3 py-1 text-xs rounded-full transition-all ${
                  language === lang.key 
                    ? 'bg-amber-500/30 text-amber-300' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="px-4 py-8 max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mb-8"
          >
            <RefreshCw className="w-8 h-8 text-amber-400 mx-auto mb-3 animate-spin" />
            <p className="text-slate-300">
              {loadingPhase === 'domains' 
                ? `Loading ${domainOverviews.length + 1}/8 domains...`
                : 'Loading your mahadasha timeline...'}
            </p>
          </motion.div>
          
          {/* Show domains as they load */}
          {domainOverviews.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {domainOverviews.map((domain, idx) => (
                <motion.div
                  key={domain.key}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 text-center"
                >
                  <span className="text-2xl">{domain.icon}</span>
                  <p className="text-xs text-slate-300 mt-1">{domain.name.split(' ')[0]}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      {!isLoading && (
        <div className="px-4 py-6 max-w-4xl mx-auto space-y-8">
          
          {/* Section 1: Domain Overviews */}
          <section>
            <h2 className="text-lg font-semibold text-amber-300 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Your Life Domains Overview
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence>
                {domainOverviews.map((domain, idx) => {
                  const Icon = DOMAIN_ICONS[domain.key] || Sparkles;
                  const isExpanded = showDomainDetails === domain.key;
                  
                  return (
                    <motion.div
                      key={domain.key}
                      custom={idx}
                      variants={domainCardVariants}
                      initial="hidden"
                      animate="visible"
                      className={`bg-gradient-to-br from-slate-800/70 to-slate-900/70 border rounded-xl overflow-hidden transition-all ${
                        isExpanded ? 'border-amber-500/50' : 'border-slate-700/50'
                      }`}
                    >
                      {/* Domain Header */}
                      <button
                        onClick={() => setShowDomainDetails(isExpanded ? null : domain.key)}
                        className="w-full p-4 flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                            <span className="text-xl">{domain.icon}</span>
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-100">{domain.name}</h3>
                            <p className="text-xs text-slate-400 line-clamp-1">
                              {domain.summary?.[language] || domain.summary?.english || ''}
                            </p>
                          </div>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {/* Expanded Details */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="border-t border-slate-700/50"
                          >
                            <div className="p-4 text-sm text-slate-300 space-y-2">
                              <p>{domain.summary?.[language] || domain.summary?.english || 'Details loading...'}</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </section>

          {/* Section 2: Mahadasha Timeline */}
          <section>
            <h2 className="text-lg font-semibold text-amber-300 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Mahadasha Timeline
            </h2>
            
            <div className="space-y-4">
              <AnimatePresence>
                {mahadashaTimeline.map((md, idx) => {
                  const isExpanded = expandedMahadasha === md.lord;
                  const color = PLANET_COLORS[md.lord] || '#edc161';
                  const icon = PLANET_ICONS[md.lord] || '☉';
                  const isCurrent = md.status === 'running';
                  
                  return (
                    <motion.div
                      key={md.lord}
                      custom={idx}
                      variants={mahadashaVariants}
                      initial="hidden"
                      animate="visible"
                      className={`rounded-xl overflow-hidden transition-all ${
                        isCurrent ? 'ring-2 ring-amber-500/50' : ''
                      }`}
                      style={{
                        background: `linear-gradient(135deg, ${color}15, rgba(15, 23, 42, 0.9))`,
                        borderLeft: `4px solid ${color}`
                      }}
                    >
                      {/* Mahadasha Header */}
                      <button
                        onClick={() => setExpandedMahadasha(isExpanded ? null : md.lord)}
                        className="w-full p-4 flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-3">
                          <span 
                            className="text-3xl"
                            style={{ textShadow: `0 0 15px ${color}` }}
                          >
                            {icon}
                          </span>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-100">{md.lord} Mahadasha</h3>
                              {isCurrent && (
                                <span className="px-2 py-0.5 text-xs bg-amber-500/30 text-amber-300 rounded-full">
                                  {language === 'hindi' ? 'वर्तमान' : language === 'hinglish' ? 'Abhi' : 'Current'}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-400">{md.years}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">
                            Age {Math.round(md.start_age)} - {Math.round(md.end_age)}
                          </span>
                          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </div>
                      </button>
                      
                      {/* Expanded: All 8 Domains for this Mahadasha */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="border-t border-slate-700/30"
                          >
                            <div className="p-4 space-y-3">
                              <p className="text-sm text-slate-300 mb-4">
                                {language === 'hindi' 
                                  ? `${md.lord} महादशा में आपके जीवन के 8 क्षेत्रों का प्रभाव:`
                                  : language === 'hinglish'
                                  ? `${md.lord} Mahadasha mein aapke life ke 8 domains ka impact:`
                                  : `Impact on your 8 life domains during ${md.lord} Mahadasha:`
                                }
                              </p>
                              
                              <div className="grid grid-cols-2 gap-2">
                                {domainOverviews.map(domain => {
                                  const insights = md.domain_insights?.[domain.key] || {};
                                  const DomainIcon = DOMAIN_ICONS[domain.key] || Sparkles;
                                  
                                  return (
                                    <motion.div
                                      key={domain.key}
                                      initial={{ opacity: 0, y: 10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ delay: domainOverviews.indexOf(domain) * 0.05 }}
                                      className="bg-slate-800/50 rounded-lg p-3"
                                    >
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm">{domain.icon}</span>
                                        <span className="text-xs font-medium text-slate-200">
                                          {domain.name.split(' ')[0]}
                                        </span>
                                      </div>
                                      <p className="text-xs text-slate-400 line-clamp-2">
                                        {insights?.summary || insights?.key_theme || 
                                          (language === 'hindi' ? 'विश्लेषण उपलब्ध है' : 
                                           language === 'hinglish' ? 'Analysis available' : 
                                           'Analysis available')}
                                      </p>
                                    </motion.div>
                                  );
                                })}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </section>

          {/* Continue Button */}
          <div className="text-center py-8">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                localStorage.setItem('past_story_viewed', 'true');
                window.location.href = '/dashboard';
              }}
              className="px-8 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-full shadow-lg shadow-amber-500/30"
            >
              {language === 'hindi' ? 'आगे बढ़ें →' : language === 'hinglish' ? 'Aage Badhein →' : 'Continue →'}
            </motion.button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PastStoryAnimated;
